import SessionStorageService from '../../../utils/SessionStorageService'
import getConfig from 'next/config'

const routeController = require('../../../routes')
const {
  publicRuntimeConfig: { DOMAIN_URL },
} = getConfig()

export const EventScreenCategory = {
  None: 'none',
  GroceryOnline: 'grocery-online',
  GroceryInstore: 'grocery-instore',
  GrocerySNG: 'grocery-sng',
  GroceryPreorder: 'grocery-preorder',
  Payment: 'payment',
  Lifestyle: 'lifestyle',
  Rewards: 'rewards',
  Omni: 'omni',
  FoodC2C: 'food-c2c',
  Membership: 'membership',
  EGifting: 'egifting',
  Account: 'account',
  Voucher: 'voucher',
}

export const EventScreenType = {
  None: '',
  All: 'all',
  Category: (slug: string, id?: string) =>
    `category | ${slug}${id ? ' | ' + id : ''}`,
  Brand: (slug: string) => 'brand | ' + slug,
  Tag: (slug: string, id?: string) => `tag | ${slug}${id ? ' | ' + id : ''}`,
  Promotion: (name: string) => name ? 'promotion | ' + name : 'promotion',
  ShoppingList: 'shopping list',
  Product: (slug: string) => 'product | ' + slug,
  ProductListing: (title: string) => 'product listing | ' + title,
  Search: (title: string) => (title ? 'search | ' + title : 'search'),
  Cart: 'cart',
  Scanner: 'scanner',
  Home: 'home',
  StoreHome: 'store home',
  Checkout: 'checkout',
  MoreTab: 'more',
  WeeklyPromotion: (name?: string) =>
    name ? `weekly promotions | ${name}` : 'weekly promotions',
  VoucherWallet: 'voucher wallet',
  Orders: 'orders',
  OrderDetails: 'order details',
}

export const ScreenSection = {
  ProductCollection: (cta: string) => `product-collection | ${cta}`,
}

export const EventScreenSection = {
  None: '',
  ProductCollection: (name: string) => `product-collection | ${name}`,
  CategoryCollection: (name: string) => `category-collection | ${name}`,
  PromotionCollection: (name: string) => `promotion-collection | ${name}`,
  VoucherCollection: (name: string) => `voucher-collection | ${name}`,
  RewardsCollection: (name: string) => `rewards-collection | ${name}`,
  Popup: (name: string) => `popup | ${name}`,
  NavigationHeader: (name: string) => `navigation-header | ${name}`,
  NavigationFooter: (name: string) => `navigation-footer | ${name}`,
  Chip: (name: string) => `chip | ${name}`,
  Widget: (name: string) => `widget | ${name}`,
  FloatingWidget: (name: string) => `floating-widget | ${name}`,
}

type EventScreenData = {
  category: string
  type: string
  section: string | null
}

function createEventScreenData(
  screenCategory: string,
  screenType: string,
  section: string | null
): EventScreenData {
  return { category: screenCategory, type: screenType, section }
}

function isEqual(
  screenData1: EventScreenData,
  screenData2: EventScreenData
): boolean {
  return (
    screenData1?.category === screenData2?.category &&
    screenData1?.type === screenData2?.type &&
    screenData1?.section === screenData2?.section
  )
}

const storageKey = 'event-screen-manager'
const maxSize = 2

function saveData(screenDataArray: EventScreenData[]) {
  SessionStorageService.setItem(storageKey, JSON.stringify(screenDataArray))
}

function loadData(): EventScreenData[] {
  const data = SessionStorageService.getItem(storageKey)
  try {
    return data
      ? JSON.parse(data).map((d: EventScreenData) =>
          createEventScreenData(d.category, d.type, d.section)
        )
      : []
  } catch (error) {
    console.warn('Failed to parse screen data from sessionStorage', error)
    return []
  }
}

export function recordScreenData(
  screenData: EventScreenData,
  callback?: () => void
) {
  const screenDataArray = loadData()
  const newData = screenData

  // Prevent duplicates by comparing with the last screenData
  if (
    screenDataArray.length === 0 ||
    !isEqual(screenDataArray[screenDataArray.length - 1], newData)
  ) {
    screenDataArray.push(newData)
    if (screenDataArray.length > maxSize) {
      screenDataArray.shift() // Keep only the last two records
    }
    saveData(screenDataArray)
    if (typeof callback === 'function') {
      callback()
    }
  }
}

export function setCurrentScreenSection(newSection: string) {
  const screenDataArray = loadData()
  if (screenDataArray.length > 0) {
    screenDataArray[screenDataArray.length - 1].section = newSection // Update the last screen's section
    saveData(screenDataArray)
  }
}

export function getCurrentScreenName(): string | null {
  const screenDataArray = loadData()
  const data = screenDataArray[screenDataArray.length - 1] ?? null

  const screenCategory = data?.category ?? null
  const screenType = data?.type ?? null

  if (screenCategory && screenType) {
    return `${screenCategory} | ${screenType}`
  }

  return screenCategory
}

export function getCurrentScreenSection(): string | null {
  const screenDataArray = loadData()
  const section =
    screenDataArray.length > 0
      ? screenDataArray[screenDataArray.length - 1].section
      : null
  return section && section !== '' ? section : null
}

export function getPreviousScreenName(): string | null {
  const screenDataArray = loadData()
  const data =
    screenDataArray.length >= 2
      ? screenDataArray[screenDataArray.length - 2]
      : null

  const screenCategory = data?.category ?? null
  const screenType = data?.type ?? null

  if (screenCategory && screenType) {
    return `${screenCategory} | ${screenType}`
  }

  return screenCategory
}

export function getPreviousScreenSection(): string | null {
  const screenDataArray = loadData()
  const section =
    screenDataArray.length >= 2
      ? screenDataArray[screenDataArray.length - 2].section
      : null
  return section && section !== '' ? section : null
}

export function resetScreenData() {
  SessionStorageService.removeItem(storageKey)
}

export function safeMatch(routeController: any, url: string) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  try {
    const parsed = new URL(url, DOMAIN_URL);

    const safeSearchParams = new URLSearchParams();
    parsed.searchParams.forEach((value, key) => {
      try {
        safeSearchParams.set(key, encodeURIComponent(decodeURIComponent(value)));
      } catch {
        safeSearchParams.set(key, encodeURIComponent(value));
      }
    });

    const safeUrl =
      parsed.pathname + (safeSearchParams.toString() ? "?" + safeSearchParams.toString() : "");

    return routeController.match(safeUrl);
  } catch (error) {
    console.error("Error matching URL:", error);
    return null;
  }
}

export const safeDecodeURIComponent = (str: string | undefined | null): string => {
  if (!str) {
    return '';
  }

  try {
    return decodeURIComponent(str);
  } catch (e) {
    console.warn('Failed to decode URI component:', str, e);
    return str;
  }
};

/**
 * Maps a given URL path to the corresponding `EventScreenData` object.
 * Uses a `switch (true)` structure to handle multiple URL patterns dynamically.
 * Returns `null` if no matching route is found.
 *
 * @param {string} url - The URL path to map.
 * @returns {EventScreenData | null} - Mapped `EventScreenData` object or `null`.
 *
 * @example
 * const screenData = mapUrlToScreenData("/home");
 * console.log(screenData);
 * // Output: { screenCategory: "omni", screenType: "home", section: "" }
 *
 * @example
 * const screenData = mapUrlToScreenData("/product/kirei-kirei-anti-bacterial-hand-soap");
 * if (screenData) {
 *     console.log(screenData.screenCategory); // "grocery-online"
 *     console.log(screenData.screenType);    // "product | kirei-kirei-anti-bacterial-hand-soap"
 * }
 *
 * @example
 * const screenData = mapUrlToScreenData("/random-path");
 * console.log(screenData); // Output: null
 */
export function mapUrlToScreenData(
  url: string | undefined | null
): EventScreenData | null {
  if (!url || typeof url !== 'string') {
    return null;
  }
  
  const match = safeMatch(routeController, url);
  if (!match) {
    return null;
  }
  
  switch (true) {
    case match.parsedUrl?.pathname === '/': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.Home,
        EventScreenSection.None
      )
    }
    case match.route?.name === 'categories': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.Category(EventScreenType.All),
        EventScreenSection.None
      )
    }
    case match.route?.name === 'category': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.Category(match.query.slug),
        EventScreenSection.None
      )
    }
    case match.route?.name === 'brands': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.Brand(EventScreenType.All),
        EventScreenSection.None
      )
    }
    case match.route?.name === 'brand': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.Brand(safeDecodeURIComponent(match.query.slug ?? '')),
        EventScreenSection.None
      )
    }
    case match.route?.name === 'promotions': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.Promotion(""),
        EventScreenSection.Chip(match.query.tag ? safeDecodeURIComponent(match.query.tag) : EventScreenType.All)
      )
    }
    case match.route?.name === 'product': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.Product(match.query.slug),
        EventScreenSection.None
      )
    }
    case match.route?.name === 'product-listing': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.ProductListing(
          safeDecodeURIComponent(match.query.title)
        ),
        EventScreenSection.ProductCollection(
          safeDecodeURIComponent(match.query.title)
        )
      )
    }
    case match.route?.name === 'cart': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.Cart,
        null
      )
    }
    case match.route?.name === 'checkout': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.Checkout,
        null
      )
    }
    case match.route?.name === 'tag': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.Tag(match.query.slug ?? ''),
        null
      )
    }
    case match.route?.name === 'shoppinglist': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.ShoppingList,
        EventScreenSection.None
      )
    }
    case match.route?.name === 'account': {
      let section = EventScreenSection.None
      if (match.query.tab === 'vouchers') {
        const filter = match?.query?.filter ?? 'all'
        section = EventScreenSection.Chip(filter.toLowerCase())
      }
      return createEventScreenData(
        EventScreenCategory.Account,
        EventScreenType.All,
        section
      )
    }
    case match.route?.name === 'search': {
      let title = ''
      if (match?.query?.query && typeof match.query.query === 'string') {
        title = match.query.query
      }
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.Search(title),
        EventScreenSection.None
      )
    }
    case match.route?.name === 'store-weekly-ad': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.WeeklyPromotion(),
        EventScreenSection.None
      )
    }
    case match.route?.name === 'orders': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.Orders,
        EventScreenSection.None
      )
    }
    case match.route?.name === 'order': {
      return createEventScreenData(
        EventScreenCategory.GroceryOnline,
        EventScreenType.OrderDetails,
        EventScreenSection.None
      )
    }
    default: {
      return null
    }
  }
}
